import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X as XIcon, Clock, ShieldCheck, Loader2, Search, MapPin, Phone, BadgeCheck, RefreshCw, ClipboardCheck, Briefcase, IdCard } from "lucide-react";
import api from "../lib/api";
import { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MODES = {
  contractor: {
    title: "Approval-Contractors",
    label: "Admin · Contractor Approval",
    isPayroll: false,
    requiredPerm: "approve_contractors",
    emptyText: "No contractors are awaiting approval.",
    testIdRoot: "contractor-approvals",
  },
  payroll: {
    title: "Approval-Payroll",
    label: "Admin · Payroll Manpower Approval",
    isPayroll: true,
    requiredPerm: "approve_payroll",
    emptyText: "No payroll registrations are awaiting approval.",
    testIdRoot: "payroll-approvals",
  },
};

export default function ContractorApprovalsPage({ mode = "contractor" }) {
  const cfg = MODES[mode];
  const { user, loading: authLoading, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectId, setRejectId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/contractors", {
        params: { status: "pending", is_payroll: cfg.isPayroll, include_pending: true },
      });
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load pending records");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, mode]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) =>
      [c.name, (c.trades || []).join(" "), c.contact_phone, c.ncc_site_name, c.city, c.state]
        .filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  async function approve(c) {
    setBusyId(c.id);
    try {
      await api.patch(`/contractors/${c.id}/status?status=approved`);
      toast.success(`Approved · ${c.name}`);
      setRows((arr) => arr.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally { setBusyId(""); }
  }
  async function submitReject() {
    if (!rejectId) return;
    setBusyId(rejectId);
    try {
      // The contractor status endpoint takes status & optional rejection_reason via query
      await api.patch(`/contractors/${rejectId}/status`, null, {
        params: { status: "rejected", rejection_reason: rejectReason || "" },
      });
      toast.success("Rejected");
      setRows((arr) => arr.filter((x) => x.id !== rejectId));
      setRejectId(""); setRejectReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(""); }
  }

  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!can(cfg.requiredPerm)) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid={`${cfg.testIdRoot}-denied`}>
        <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal">
          <ShieldCheck className="mx-auto h-10 w-10 text-slate-700" />
          <h1 className="mt-4 font-heading text-2xl font-black uppercase">Access denied</h1>
          <p className="mt-2 text-slate-600">You need the <b>{cfg.requiredPerm}</b> permission to see this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8" data-testid={`${cfg.testIdRoot}-page`}>
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="industrial-label text-ncc-gold2">// {cfg.label}</div>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">
            <span className="relative inline-flex items-center gap-2 rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid={`${cfg.testIdRoot}-heading-badge`}>
              <ClipboardCheck className="h-6 w-6 md:h-8 md:w-8" />
              {rows.length > 0 && (
                <span
                  data-testid={`${cfg.testIdRoot}-heading-count`}
                  className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full border-2 border-red-700 bg-red-600 px-1.5 font-mono text-xs font-bold text-white md:h-7 md:min-w-[28px] md:text-sm"
                >
                  {rows.length}
                </span>
              )}
            </span>
            <span>{cfg.title}</span>
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {cfg.isPayroll ? "Payroll Manpower" : "Contractor"} registrations are <b>pending</b> until you approve them.
            Rejecting requires a reason that&apos;s emailed to the registrant.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid={`${cfg.testIdRoot}-refresh`}
          className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:-translate-y-0.5 hover:shadow-brutal-sm"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, trade, mobile, site, city…"
            data-testid={`${cfg.testIdRoot}-search`}
            className="w-full border-2 border-slate-300 bg-white py-2 pl-9 pr-3 font-mono text-sm focus:border-slate-900 focus:outline-none"
          />
        </div>
        <div className="industrial-label text-slate-600" data-testid={`${cfg.testIdRoot}-count`}>
          {loading ? "Loading…" : `${visible.length} pending`}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="grid place-items-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid={`${cfg.testIdRoot}-empty`}>
            <BadgeCheck className="h-10 w-10 text-emerald-500" />
            <div className="font-heading text-lg font-bold text-slate-700">All caught up</div>
            <div className="font-mono text-xs text-slate-500">{cfg.emptyText}</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid={`${cfg.testIdRoot}-list`}>
            {visible.map((c) => (
              <li
                key={c.id}
                data-testid={`${cfg.testIdRoot}-row-${c.id}`}
                className="flex flex-col gap-3 border-2 border-amber-300 bg-white p-4 shadow-brutal-sm sm:flex-row sm:items-center"
              >
                <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-amber-400 bg-slate-100">
                  {c.photo?.path ? (
                    <img src={fileUrl(c.photo.path)} alt={c.name} className="h-full w-full object-cover" />
                  ) : (
                    <Clock className="h-6 w-6 text-amber-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-lg font-extrabold text-slate-900">{c.name}</h3>
                    <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                      <Clock className="h-3 w-3" /> PENDING
                    </span>
                    {(c.trades || []).slice(0, 2).map((t) => (
                      <span key={t} className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 industrial-label text-[10px]">{t}</span>
                    ))}
                  </div>
                  <div className="mt-1 grid grid-cols-1 gap-1 font-mono text-xs text-slate-700 sm:grid-cols-3">
                    <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-ncc-navy" /> {c.contact_phone || "—"}</div>
                    <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-ncc-navy" /> {[c.city, c.state].filter(Boolean).join(", ") || "—"}</div>
                    <div className="truncate">
                      <Briefcase className="mr-1 inline h-3 w-3 text-ncc-navy" />
                      {c.ncc_site_name || <span className="text-amber-600">No site</span>}
                    </div>
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-slate-500">
                    {c.id_proof_type && c.id_proof_number ? (
                      <><IdCard className="mr-1 inline h-3 w-3" />{c.id_proof_type}: {c.id_proof_number.slice(0, 4)}••{c.id_proof_number.slice(-2)}</>
                    ) : null}
                    {c.crew_size ? ` · Crew ${c.crew_size}` : ""}
                    {c.experience_years ? ` · Exp ${c.experience_years}y` : ""}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => approve(c)}
                    disabled={busyId === c.id}
                    data-testid={`${cfg.testIdRoot}-approve-${c.id}`}
                    className="inline-flex items-center gap-1.5 border-2 border-emerald-600 bg-emerald-600 px-3 py-2 industrial-label text-[11px] text-white hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50"
                  >
                    <Check className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => { setRejectId(c.id); setRejectReason(""); }}
                    data-testid={`${cfg.testIdRoot}-reject-${c.id}`}
                    className="inline-flex items-center gap-1.5 border-2 border-red-500 bg-white px-3 py-2 industrial-label text-[11px] text-red-700 hover:bg-red-50"
                  >
                    <XIcon className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRejectId("")}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid={`${cfg.testIdRoot}-reject-modal`}>
            <div className="border-b-2 border-slate-900 bg-red-600 px-4 py-3 text-white">
              <div className="industrial-label">Reject {cfg.isPayroll ? "Payroll Manpower" : "Contractor"}</div>
            </div>
            <div className="p-4 space-y-2">
              <div className="font-mono text-xs text-slate-600">Reason (sent to the registrant via email):</div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid={`${cfg.testIdRoot}-reject-reason`}
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                placeholder="e.g. Aadhaar photo blurry — please re-upload"
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
              <button type="button" onClick={() => setRejectId("")} className="border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-[11px]">Cancel</button>
              <button type="button" onClick={submitReject} disabled={busyId === rejectId} data-testid={`${cfg.testIdRoot}-reject-confirm`} className="border-2 border-red-600 bg-red-600 px-3 py-1.5 industrial-label text-[11px] text-white disabled:opacity-50">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
